import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Navigation, Clock3 } from "lucide-react";
import {
  PHONE_MAIN, PHONE_MAIN_TEL, PHONE_SECOND, PHONE_SECOND_TEL,
  PHONE_LANDLINE, PHONE_LANDLINE_TEL, EMAIL,
} from "@/lib/contact";

const MAPS_URL = "https://www.google.com/maps?q=Bulevardul+General+Ion+Dragalina+27,+Timi%C8%99oara&output=embed";
const DIRECTIONS_URL = "https://www.google.com/maps/dir/?api=1&destination=Bulevardul+General+Ion+Dragalina+27,+Timi%C8%99oara";

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 lg:py-28 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12"
        >
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#FF2A3B]">Contact & locație</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight">
            Treci pe la <span className="text-[#0055FF]">magazin</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 flex flex-col gap-4"
          >
            <div
              data-testid="contact-business-card"
              className="relative overflow-hidden border border-white/15 bg-gradient-to-br from-[#0055FF] via-[#0A3BD0] to-[#07122B] p-7 flex-1 flex flex-col"
            >
              <span className="absolute -bottom-10 -right-4 font-display text-[150px] leading-none font-black text-white/[0.06] select-none pointer-events-none">
                SP
              </span>
              <p className="font-display text-[34px] leading-[0.95] font-black uppercase tracking-tight text-white">
                Speed Shop
                <span className="block text-transparent [-webkit-text-stroke:1.5px_rgba(255,255,255,0.9)]">Power</span>
              </p>
              <span className="mt-2 text-[10px] font-mono uppercase tracking-[0.3em] text-white/60">
                Carte de vizită · Magazin piese auto
              </span>

              <div className="relative mt-7 space-y-5 text-sm flex-1">
                <div className="flex gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-white/80" />
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/60">Adresa</p>
                    <p className="mt-0.5 font-semibold text-white">G. Dragalina nr. 27, Timișoara</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Clock3 className="h-4 w-4 mt-0.5 shrink-0 text-white/80" />
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/60">Orar</p>
                    <p className="mt-0.5 text-white/90">Luni – Vineri&nbsp;&nbsp;09:00 – 17:00</p>
                    <p className="text-white/90">Sâmbătă&nbsp;&nbsp;09:00 – 12:00</p>
                    <p className="text-white/90">Duminică&nbsp;&nbsp;Închis</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Phone className="h-4 w-4 mt-0.5 shrink-0 text-white/80" />
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/60">Telefon</p>
                    <a data-testid="contact-phone-1" href={`tel:${PHONE_MAIN_TEL}`} className="mt-0.5 block font-display text-lg font-bold text-white hover:text-[#FFD166] transition-colors">{PHONE_MAIN}</a>
                    <a data-testid="contact-phone-2" href={`tel:${PHONE_SECOND_TEL}`} className="block font-display text-lg font-bold text-white hover:text-[#FFD166] transition-colors">{PHONE_SECOND}</a>
                    <a data-testid="contact-phone-3" href={`tel:${PHONE_LANDLINE_TEL}`} className="block font-display text-lg font-bold text-white hover:text-[#FFD166] transition-colors">{PHONE_LANDLINE}</a>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Mail className="h-4 w-4 mt-0.5 shrink-0 text-white/80" />
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/60">E-mail</p>
                    <a data-testid="contact-email-link" href={`mailto:${EMAIL}`} className="mt-0.5 block font-semibold text-white hover:text-[#FFD166] transition-colors break-all">
                      {EMAIL}
                    </a>
                  </div>
                </div>
              </div>

              <a
                data-testid="directions-cta"
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="relative mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-white transition-colors"
              >
                <Navigation className="h-4 w-4" /> Obține indicații de orientare
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 border border-white/10 bg-[#131927] overflow-hidden min-h-[420px]"
          >
            <iframe
              data-testid="contact-map-embed"
              title="Harta SC Speed Shop Power SRL"
              src={MAPS_URL}
              className="w-full h-full min-h-[420px] map-dark border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 flex flex-col gap-4"
          >
            <div className="relative flex-1 min-h-[200px] overflow-hidden border border-white/10 group">
              <img
                data-testid="contact-photo-exterior"
                src="/images/img5.webp"
                alt="Exteriorul magazinului SC Speed Shop Power SRL de pe Dragalina 27"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute bottom-3 left-3 bg-[#07122B]/85 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-white">
                Exterior · Dragalina 27
              </span>
            </div>
            <div className="relative flex-1 min-h-[200px] overflow-hidden border border-white/10 group">
              <img
                data-testid="contact-photo-interior"
                src="/images/img2.jpg"
                alt="Interiorul magazinului — rafturi cu piese, uleiuri și baterii"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute bottom-3 left-3 bg-[#07122B]/85 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-white">
                Interior magazin
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
